/**
 * 区域1 · 导航栏：玻璃态固定顶栏，发光魔方 Logo，激活项底部发光指示条，滚动加深模糊
 */
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { navigate, type Page } from '../hooks/useHashRoute';

const ITEMS: { page: Page; label: string; hash: string }[] = [
  { page: 'home', label: '首页', hash: '#home' },
  { page: 'learn', label: '教学中心', hash: '#learn' },
  { page: 'play', label: '自由练习', hash: '#play' },
];

export function Nav({ active }: { active: Page }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: scrolled ? 'rgba(6,6,10,0.78)' : 'rgba(8,8,14,0.35)',
        backdropFilter: scrolled ? 'blur(26px)' : 'blur(12px)',
        WebkitBackdropFilter: scrolled ? 'blur(26px)' : 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-3 py-3 sm:px-5 sm:py-3.5">
        <button
          className="flex cursor-pointer items-center gap-2 border-none bg-transparent p-0 whitespace-nowrap sm:gap-2.5"
          onClick={() => navigate('#home')}
        >
          <LogoCube />
          <span
            className="text-[15px] font-extrabold tracking-wide whitespace-nowrap text-white sm:text-[17px]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            魔方学院
          </span>
        </button>

        <nav className="flex items-center gap-0 sm:gap-1">
          {ITEMS.map((it) => {
            const isActive = active === it.page;
            return (
              <button
                key={it.page}
                onClick={() => navigate(it.hash)}
                className={`relative cursor-pointer rounded-lg border-none bg-transparent px-2.5 py-2 text-[13px] font-semibold whitespace-nowrap transition-colors sm:px-4 sm:text-[14px] ${
                  isActive ? 'text-white' : 'text-white/55 hover:text-white/85'
                }`}
              >
                {it.label}
                {isActive && (
                  <motion.span
                    layoutId="nav-indicator"
                    className="absolute inset-x-3 -bottom-[3px] h-[2px] rounded-full"
                    style={{
                      background: 'linear-gradient(90deg, #00e5ff, #007aff)',
                      boxShadow: '0 0 10px rgba(0,229,255,0.8), 0 0 22px rgba(0,229,255,0.4)',
                    }}
                    transition={{ type: 'spring', stiffness: 500, damping: 38 }}
                  />
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </header>
  );
}

/** 发光 2×2 迷你魔方 Logo（纯 CSS） */
function LogoCube() {
  const colors = ['#ff3b30', '#ffcc00', '#34c759', '#007aff'];
  return (
    <span
      className="grid h-7 w-7 grid-cols-2 gap-[3px] rounded-[7px] p-[3px]"
      style={{
        background: 'rgba(255,255,255,0.06)',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.12), 0 0 14px rgba(0,229,255,0.35)',
      }}
    >
      {colors.map((c) => (
        <span
          key={c}
          className="rounded-[3px]"
          style={{ background: c, boxShadow: `0 0 6px ${c}88` }}
        />
      ))}
    </span>
  );
}
